import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { licenseService } from '../../services/licenseService';
import type { TenantLicense } from '../../types/tenantLicense';
import {
  resolvePostLoginPath,
  resolveSystemSelectionTarget,
} from '../../utils/postLoginNavigation';
import { resolveHomePageForUser } from '../../utils/loginRedirectUtils';
import { useAuth } from '../../contexts/AuthContext';

export const SystemSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [license, setLicense] = useState<TenantLicense | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadLicense() {
      setLoading(true);
      try {
        const cached = licenseService.getCachedLicense();
        const resolved = cached ?? (await licenseService.fetchMyLicense());
        licenseService.cacheLicense(resolved);
        setLicense(resolved);

        if (!resolved.requiresSystemSelection || resolved.moduleCodes.length <= 1) {
          const target = resolvePostLoginPath(resolved, {
            isAdmin: user?.isAdmin,
            roleCode: user?.role?.code,
          });
          navigate(target, { replace: true });
        }
      } catch {
        navigate(
          resolveHomePageForUser({
            isAdmin: user?.isAdmin,
            roleCode: user?.role?.code,
          }),
          { replace: true }
        );
      } finally {
        setLoading(false);
      }
    }

    loadLicense();
  }, [navigate, user?.isAdmin, user?.role?.code]);

  const handleSelect = (moduleCode: string) => {
    if (!license) {
      return;
    }

    const target = resolveSystemSelectionTarget(license, moduleCode);
    if (target) {
      navigate(target, { replace: true });
      return;
    }

    navigate(
      resolveHomePageForUser({
        isAdmin: user?.isAdmin,
        roleCode: user?.role?.code,
      }),
      { replace: true }
    );
  };

  if (loading || !license) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold mb-2">Escolha o sistema</h1>
        <p className="text-gray-600 mb-8">
          Seu plano inclui mais de um produto. Selecione por onde deseja começar.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {license.moduleCodes.map(code => {
            const label = code.toUpperCase() === 'KNOW' ? 'Know InDoc' : code;
            const description =
              code.toUpperCase() === 'KNOW'
                ? 'Gestão documental e workflow'
                : code.toUpperCase() === 'BTALK'
                  ? 'Comunicação e atendimento'
                  : 'Acessar módulo';

            return (
              <button
                key={code}
                type="button"
                onClick={() => handleSelect(code)}
                className="text-left border rounded-lg p-5 hover:border-blue-500 hover:shadow transition"
              >
                <span className="block text-lg font-semibold text-gray-900">{label}</span>
                <span className="block text-sm text-gray-500 mt-1">{description}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
